import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useDashboardStore from '../../stores/dashboardStore';

const FREEZE_DAYS = 30;

// Danger zone card: request account deletion (30-day freeze, then hard purge)
// and cancel a pending deletion. Rendered below MatchingSettings, never
// blurred behind the profile-completion gate — an incomplete profile must
// still be able to delete itself.
const AccountDangerZone = () => {
    const { t, i18n } = useTranslation();

    const formData = useDashboardStore(s => s.formData);
    const modals = useDashboardStore(s => s.modals);
    const openModal = useDashboardStore(s => s.openModal);
    const closeModal = useDashboardStore(s => s.closeModal);
    const deleteAccount = useDashboardStore(s => s.deleteAccount);
    const cancelDeletion = useDashboardStore(s => s.cancelDeletion);

    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const deletionScheduledAt = formData.deletionScheduledAt;
    const purgeDateStr = deletionScheduledAt
        ? new Date(new Date(deletionScheduledAt).getTime() + FREEZE_DAYS * 24 * 60 * 60 * 1000)
            .toLocaleDateString(i18n.language === 'ru' ? 'ru-RU' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })
        : '';

    const handleConfirmDelete = async () => {
        setBusy(true);
        setError('');
        const ok = await deleteAccount();
        if (!ok) setError(t('dashboard.danger_zone.request_failed', 'Something went wrong. Please try again.'));
        setBusy(false);
    };

    const handleCancelDeletion = async () => {
        setBusy(true);
        setError('');
        const ok = await cancelDeletion();
        if (!ok) setError(t('dashboard.danger_zone.request_failed', 'Something went wrong. Please try again.'));
        setBusy(false);
    };

    // --- Pending deletion: amber banner with cancel ---
    if (deletionScheduledAt) {
        return (
            <div className="glass-card" style={{ padding: '1.5rem', border: '1px solid rgba(245, 158, 11, 0.5)', background: 'rgba(255, 251, 235, 0.85)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                    <span style={{ fontSize: '1.25rem' }}>⏳</span>
                    <h2 className="section-title" style={{ marginBottom: 0, color: '#b45309' }}>
                        {t('dashboard.danger_zone.pending_title', 'Deletion scheduled')}
                    </h2>
                </div>
                <p style={{ fontSize: '0.9rem', color: '#78350f', lineHeight: '1.5', textAlign: 'left', margin: '0 0 1rem' }}>
                    {t('dashboard.danger_zone.pending_text', 'Your account and all data will be permanently deleted on {{date}}. Until then you won\'t receive any messages.', { date: purgeDateStr })}
                </p>
                <button
                    onClick={handleCancelDeletion}
                    disabled={busy}
                    style={{
                        width: '100%', padding: '0.75rem 1rem',
                        background: '#f59e0b', border: 'none', borderRadius: '8px',
                        fontSize: '0.95rem', fontWeight: '600', color: 'white',
                        cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1, transition: 'all 0.2s'
                    }}
                >
                    {t('dashboard.danger_zone.cancel_deletion_btn', '↩️ Cancel deletion')}
                </button>
                {error && (
                    <p style={{ color: '#ef4444', fontSize: '0.85rem', margin: '0.75rem 0 0', textAlign: 'left' }}>{error}</p>
                )}
            </div>
        );
    }

    // --- Default: danger zone with delete button + confirm modal ---
    return (
        <div className="glass-card" style={{ padding: '1.5rem' }}>
            <h2 className="section-title" style={{ marginBottom: '0.75rem', color: '#991b1b' }}>
                {t('dashboard.danger_zone.title', 'Danger zone')}
            </h2>
            <p style={{ fontSize: '0.85rem', color: '#888', lineHeight: '1.5', textAlign: 'left', margin: '0 0 1rem' }}>
                {t('dashboard.danger_zone.delete_description', 'Deleting your account stops all messages and matches immediately. After a 30-day grace period your profile, matches and avatar are permanently erased. You can cancel anytime during those 30 days.')}
            </p>
            <button
                onClick={() => { setError(''); openModal('deleteAccount'); }}
                style={{
                    width: '100%', padding: '0.75rem 1rem',
                    background: 'transparent', border: '1px solid #ef4444', borderRadius: '8px',
                    fontSize: '0.95rem', fontWeight: '600', color: '#ef4444',
                    cursor: 'pointer', transition: 'all 0.2s'
                }}
                onMouseOver={(e) => { e.target.style.background = '#fef2f2'; }}
                onMouseOut={(e) => { e.target.style.background = 'transparent'; }}
            >
                {t('dashboard.danger_zone.delete_btn', 'Delete account')}
            </button>
            {error && (
                <p style={{ color: '#ef4444', fontSize: '0.85rem', margin: '0.75rem 0 0', textAlign: 'left' }}>{error}</p>
            )}

            {/* Confirmation modal */}
            {modals.deleteAccount && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1000,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                    <div style={{
                        background: 'white', borderRadius: '16px', padding: '2rem',
                        maxWidth: '400px', width: '90%', boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)'
                    }}>
                        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🗑</div>
                            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937', marginBottom: '0.5rem' }}>
                                {t('dashboard.danger_zone.confirm_title', 'Delete your account?')}
                            </h3>
                            <p style={{ color: '#6b7280', fontSize: '0.95rem', lineHeight: '1.5' }}>
                                {t('dashboard.danger_zone.confirm_text', 'Your current match will be cancelled and all messages will stop. In 30 days your data is erased for good. You can cancel the deletion anytime before that.')}
                            </p>
                        </div>
                        <div style={{ display: 'flex', gap: '0.75rem' }}>
                            <button
                                onClick={() => closeModal('deleteAccount')}
                                style={{
                                    flex: 1, padding: '0.75rem 1rem',
                                    background: '#f3f4f6', border: 'none', borderRadius: '8px',
                                    fontSize: '0.95rem', fontWeight: '600', color: '#6b7280',
                                    cursor: 'pointer', transition: 'all 0.2s'
                                }}
                            >
                                {t('common.cancel', 'Cancel')}
                            </button>
                            <button
                                onClick={handleConfirmDelete}
                                disabled={busy}
                                style={{
                                    flex: 1, padding: '0.75rem 1rem',
                                    background: '#ef4444', border: 'none', borderRadius: '8px',
                                    fontSize: '0.95rem', fontWeight: '600', color: 'white',
                                    cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1, transition: 'all 0.2s'
                                }}
                            >
                                {t('dashboard.danger_zone.confirm_btn', 'Delete account')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AccountDangerZone;
